import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect
} from "react";
import {
  Drawer,
  Button,
  Form,
  Input,
  Radio,
  notification,
  Select,
  Popconfirm,
  Popover
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from 'react-redux';
import UserAPI from '../../../restAPI/UserAPI';
import validationRules from '../../../assets/validation/rules';
import validateForm from '../../../utils/FormValidation';

const popoverContentType = (
  <div>
1. <b>Group Admin:</b> Full permissions &
group-level access <br/>
2. <b>Company Admin:</b> Partial
permissions & company-level
access only <br/>
3. <b>General User:</b> Only certain
permissions
  </div>
);
const Context = React.createContext({
  name: "Default",
});

const StepForm = forwardRef(({ onRefresh }, ref) => {
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const { token } = useSelector(state => state.auth);
  const [userTypes, setUserTypes] = useState([]);
  const [isGetUserTypes, setIsGetUserTypes] = useState(true);
  const [isModified, setIsModified] = useState(false);
  const [showPopconfirm, setShowPopconfirm] = useState(false);
  const [cancelButtonDisabled, setCancelButtonDisabled] = useState(false);
  const [payload, setPayload] = useState({
    "_id": "",
    "firstName": "",
    "lastName": "",
    "email": "",
    "roleId": "",
    "role": "",
    "groupId": localStorage.getItem('groupId'),
    "companyId": localStorage.getItem('companyId'),
    "status": 1
  });
  const [errors, setErrors] = useState({});

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement
  });
  };

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    openEditDrawer: () => {
      setPayload({_id: ref.id});
      getUserById(ref.id);
      setVisible(true); // Open drawer
    },
    closeEditDrawer: () => {
      setVisible(false); // Close drawer
      if (onRefresh) onRefresh(); // Trigger refresh on close
    },
  }));

  const getUserById = async (id) => {   
    await UserAPI.getUserById(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), id).then((res) => {
        if (res?.data) {
          //console.log(JSON.stringify(res.data));
          let status = 1;
          if(res.data.status){
            status = 1;
          }
          else{
            status = 0;
          }
          setPayload({
            "_id": res.data._id,
            "firstName": res.data.firstName,
            "lastName": res.data.lastName,
            "email": res.data.email,
            "roleId": res.data.role.id,
            "role": res.data.role.name,
            "groupId": res.data.groupId,
            "companyId": res.data.companyId,
            "status": status
          });
          //console.log(JSON.stringify(res.data.status));
        }
    }).catch(err => {
        console.log("error getting user data :", err.message);
    })
  };

  const contextValue = useMemo(
    () => ({
      name: "Ant Design",
    }),
    []
  );

  const onClose = () => {
    setVisible(false);
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    setPayload({
      "_id": "",
      "firstName": "",
      "lastName": "",
      "email": "",
      "roleId": "",
      "role": "",
      "groupId": localStorage.getItem('groupId'),
      "companyId": localStorage.getItem('companyId'),
      "status": 1
    });
    setErrors({});
    setIsModified(false);
  } 

  useEffect(() => {
    if(isGetUserTypes){
      getAllUserTypes();
      setIsGetUserTypes(false);
    }
  });

  const getAllUserTypes = async () => {   
    await UserAPI.getAllUserTypes(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId')).then((res) => {
        if (res?.data) {
          //console.log(JSON.stringify(res.data));
          const desiredOrder = ["Group Admin", "Company Admin", "General User"];
          res?.data.sort((a, b) => 
            desiredOrder.indexOf(a.name) - desiredOrder.indexOf(b.name)
          );
          const rows = [];
          res.data.map((rowValue) =>  {
              rows.push({value: rowValue._id, label: rowValue.name})
          });
          setUserTypes(rows);

        }
    }).catch(err => {
        console.log("error getting user types data :", err.message);
    })
  };

  const OnUserTypeChange = (value) => {
    setPayload({
      "_id": payload._id,
      "firstName": payload.firstName,
      "lastName": payload.lastName,
      "email": payload.email,
      "roleId": value,
      "role": payload.role,
      "groupId": payload.groupId,
      "companyId": payload.companyId,
      "status": payload.status
    });
    setErrors({});
    setIsModified(true);
  }

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules)
    if (!validationResult.isValid) {
        const err = validationResult.errors
        const newErrors = {};
        Object.keys(err).forEach((value) => {
            newErrors[value] = err[value];
        })
        setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
        const keyData = Object.keys(data)[0]
        setErrors(prev => ({ ...prev, [keyData]: '' }))
    }
  }

  const onRadioChange = (e) => {
    setPayload({
      "_id": payload._id,
      "firstName": payload.firstName,
      "lastName": payload.lastName,
      "email": payload.email,
      "roleId": payload.roleId,
      "role": payload.role,
      "groupId": payload.groupId,
      "companyId": payload.companyId,
      "status": e.target.value
    });
    setIsModified(true);
  };

  const onClearChange = () => {
    getUserById(payload._id);
    setErrors({});
    setIsModified(false);
  }

  const validateBeforeSubmit = () => {
    const validateData = {
      userFirstName: payload.firstName,
      userLastName: payload.lastName,
      email: payload.email,
      userType: payload.roleId,
    }
    const validationResult = validateForm(validateData, validationRules);
  
    if (!validationResult.isValid) {
        const validationErr = validationResult.errors;
        const newErrors = {};
        Object.keys(validationErr).forEach((value) => {
            newErrors[value] = validationErr[value];
        })
        setErrors(newErrors);
        return false;
    } else {
        return true;
    }
  }

  const onSubmit = async () => {
    if(localStorage.getItem('groupId') === null){
      openNotificationWithIcon('error', 'User update failed', 'Please select the group', 'bottomLeft');
    }else{
      if(localStorage.getItem('companyId') === null){
        if(payload.role === "General User" || payload.role === "Company Admin"){
          openNotificationWithIcon('error', 'User update failed', 'Please select the company', 'bottomLeft');
        }else if(payload.role === "Group Admin"){
          updateUser();
        }
      }
      else{
          updateUser();
        }
      }
  }

  const updateUser = async () => {
    setShowPopconfirm(false);
    const formData = {
      "firstName": payload.firstName,
      "lastName": payload.lastName,
      "email": payload.email,
      "roleId": payload.roleId,
      "groupId": localStorage.getItem('groupId'),
      "companyId": localStorage.getItem('companyId'),
      "status": payload.status
    };

    await UserAPI.updateUser(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId'), payload._id, formData)
    .then((response) => {
        //console.log(response);
        onClose();
        openNotificationWithIcon('success', "Successful", `${response.message}`, 'bottomLeft');
    })
    .catch((err) => {
        if (err.response) {
            openNotificationWithIcon('error', 'User update failed', `${err.response.data.message}`, 'bottomLeft');
        } else {
            openNotificationWithIcon('error', 'User update failed', `${err.message}`, 'bottomLeft');
        }
    });
  }

  const handleCreateUpdateClick = (e) => {
    e.preventDefault();
    let validation = validateBeforeSubmit();
    if (validation) {
      setShowPopconfirm(true)
    } else {
      setShowPopconfirm(false)
    }
  }

  const handleCancel = () => {
    setCancelButtonDisabled(true);
    setTimeout(() => {
        setShowPopconfirm(false);
        setCancelButtonDisabled(false);
    }, 300);
  };

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Drawer
        title="Update User"
        width={700}
        visible={visible}
        onClose={onClose}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button style={{ marginRight: 8 }} size="large" onClick={onClearChange}>
            Reset
            </Button>

            <Popconfirm
                title={`Do you want to update the user details?`}
                open={showPopconfirm}
                onConfirm={() => onSubmit()}
                onCancel={handleCancel}
                okText="Yes"
                cancelText="No"
                cancelButtonProps={{ disabled: cancelButtonDisabled }}
              >
              <Button type="primary" size="large" disabled={!isModified} onClick={handleCreateUpdateClick}>
                Update
              </Button> 
              </Popconfirm>

          </div>
        }
      >
        <div style={{ marginTop: 24 }}>
          <Form layout="vertical">
            {/* <Alert message="* Fields are required" type="warning" /> */}
            <Form.Item label="First Name" validateStatus={errors.userFirstName ? 'error' : ''} help={errors.userFirstName || ''} required>
              <Input size="large" name="firstName" value={payload["firstName"]} onChange={(e) => { setPayload(prev => ({...prev, firstName: e.target.value})); const validateData = {userFirstName: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="First Name" />
            </Form.Item>

            <Form.Item label="Last Name" validateStatus={errors.userLastName ? 'error' : ''} help={errors.userLastName || ''} required>
              <Input size="large" name="lastName" value={payload["lastName"]} onChange={(e) => { setPayload(prev => ({...prev, lastName: e.target.value})); const validateData = {userLastName: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="Last Name" />
            </Form.Item>

            
            <Form.Item label="Email Address" validateStatus={errors.email ? 'error' : ''} help={errors.email || ''} required>
              <Input size="large" name="email" disabled value={payload["email"]} onChange={(e) => { setPayload(prev => ({...prev, email: e.target.value})); const validateData = {email: e.target.value}; validateFormData(validateData); setIsModified(true); }} placeholder="Email Address" />
            </Form.Item>

            <Form.Item
               label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  User Type
                  <Popover content={popoverContentType} title="User Type" trigger="hover">
                    <InfoCircleOutlined className="info-icon" />
                  </Popover>
                </div>
              } validateStatus={errors.userType ? 'error' : ''} help={errors.userType || ''}  required >
              <Select
                showSearch
                name="userType"
                placeholder="Select User Type"
                optionFilterProp="label"
                value={payload["roleId"] || "Select a User Type"}
                size="large"
                onChange={OnUserTypeChange}
                options={userTypes}
                disabled
              />
            </Form.Item>
            <Form.Item label="Status" required>
              <Radio.Group name="status" value={payload["status"]} onChange={onRadioChange}>
                <Radio value={1}>Active</Radio>
                <Radio value={0}>Inactive</Radio>
              </Radio.Group>
            </Form.Item>
          </Form>
        </div>
      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
